export const SYSTEM_TIME_EXPIRED = 'SYSTEM_TIME_EXPIRED';
export const SYSTEM_TIME_EXPIRED_DISPLAY = 'System Time Expired';
export const RESERVED_TIMEOUT_MESSAGE = 'System Time Expired is reserved for system timer expiry. Please enter your own response.';
export const isReservedClientTimeoutText = (text) =>
  ['time out', 'timeout'].includes(String(text ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase());
export const isSystemTimeExpired = (record, value) => {
  if (record?.system_state === SYSTEM_TIME_EXPIRED || record?.timeout_state === SYSTEM_TIME_EXPIRED) return true;
  if (record?.timed_out === true || record?.timer_expired === true) return true;
  return ['time out', 'timeout', 'time-out', 'system time expired', 'system_time_expired', 'undisclosed due to timeout'].includes(
    String(value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase()
  );
};

// Presentation only; canonical storage and aggregate keys stay compatible.
export const displayResponseStatus = (value) =>
  isSystemTimeExpired({}, value) ? SYSTEM_TIME_EXPIRED_DISPLAY : value;
export const canonicalTimeoutCategory = (value, canonical = 'Timeout') =>
  isSystemTimeExpired({}, value) ? canonical : value;

export const phase4ExpiredCategories = (item) => ({
  coping_mechanism: isPhase4FieldTimedOut(item, 'what_is_it') ? 'Timeout' : null,
  thematic_domains: (item?.words || []).map((_, index) =>
    isPhase4DescriptorTimedOut(item, index) ? 'Timeout' : null),
});

export const normalizePhase3Valence = (word, index) => {
  const classification = word?.classifications?.[index];
  // An explicit classification is reliable evidence (including the manual
  // "Time Out" dropdown). Broad historical flags are handled per slot below.
  if (isSystemTimeExpired({}, classification)) return 'Timeout';
  if (isPhase3SlotTimedOut(word, index)) return 'Timeout';
  if (classification === 'Undisclosed/Timeout') return 'Undisclosed';
  return ['Positive', 'Negative', 'Neutral', 'Undisclosed'].includes(classification)
    ? classification
    : null;
};

export const getPhase3ClassificationCounts = (words = []) => {
  const counts = { Positive: 0, Negative: 0, Neutral: 0, Undisclosed: 0, Timeout: 0 };
  (words || []).filter(Boolean).forEach(word => {
    const slotCount = Math.max(
      word?.associations?.length || 0,
      word?.classifications?.length || 0,
      word?.system_states?.length || 0,
      Array.isArray(word?.timed_out) ? word.timed_out.length : 0,
    );
    for (let index = 0; index < slotCount; index += 1) {
      const valence = normalizePhase3Valence(word, index);
      if (valence) counts[valence] += 1;
    }
  });
  return counts;
};

// Phase 3 timeout is a slot property. Populated client text always wins over
// legacy/regressed timeout flags, including a word-level marker.
export const isPhase3SlotTimedOut = (word, index) => {
  const association = word?.associations?.[index];
  if (String(association ?? '').trim() && !isReservedClientTimeoutText(association)) return false;
  return isSystemTimeExpired({
    system_state: word?.system_states?.[index],
    timed_out: word?.timed_out?.[index],
  }, word?.classifications?.[index]) || isSystemTimeExpired(word);
};

export const getPhase3TimedOutSlots = (associations, timerExpired) =>
  (associations || []).map((association) => Boolean(timerExpired) && !String(association ?? '').trim());

export const createPhase4TimeoutSlots = (currentItem, requiredDescriptorCount) => {
  const item = String(currentItem?.name ?? '').trim();
  const whatIsIt = String(currentItem?.what_is_it ?? '').trim();
  const words = [...(currentItem?.words || [])];
  const pendingWord = String(currentItem?.currentWord ?? '').trim();
  if (pendingWord && !isReservedClientTimeoutText(pendingWord)) words.push(pendingWord);
  const requiredCount = Math.max(0, Number(requiredDescriptorCount) || 0);
  const existingFieldStates = currentItem?.field_system_states || {};
  const existingFieldTimedOut = currentItem?.field_timed_out || {};
  const fieldSystemStates = {
    item: existingFieldStates.item === SYSTEM_TIME_EXPIRED || existingFieldTimedOut.item === true ? SYSTEM_TIME_EXPIRED : null,
    what_is_it: existingFieldStates.what_is_it === SYSTEM_TIME_EXPIRED || existingFieldTimedOut.what_is_it === true ? SYSTEM_TIME_EXPIRED : null,
  };
  const descriptorSystemStates = words.map((word, index) => (
    index < requiredCount && !String(word ?? '').trim()
      ? SYSTEM_TIME_EXPIRED
      : null
  ));
  if (!item) fieldSystemStates.item = SYSTEM_TIME_EXPIRED;
  else fieldSystemStates.item = null;
  if (!whatIsIt) fieldSystemStates.what_is_it = SYSTEM_TIME_EXPIRED;
  else fieldSystemStates.what_is_it = null;
  while (words.length < requiredCount) {
    words.push('');
    descriptorSystemStates.push(SYSTEM_TIME_EXPIRED);
  }
  return {
    item,
    what_is_it: whatIsIt,
    words,
    field_system_states: fieldSystemStates,
    field_timed_out: Object.fromEntries(Object.entries(fieldSystemStates).map(([key, value]) => [key, value === SYSTEM_TIME_EXPIRED])),
    descriptor_system_states: descriptorSystemStates,
    descriptor_timed_out: descriptorSystemStates.map(value => value === SYSTEM_TIME_EXPIRED),
    required_descriptor_count: requiredCount,
  };
};

export const isPhase4FieldTimedOut = (item, field) => {
  const value = field === 'item' ? item?.item : item?.what_is_it;
  if (isSystemTimeExpired({}, value)) return true;
  if (String(value ?? '').trim() && !isReservedClientTimeoutText(value)) return false;
  return item?.field_system_states?.[field] === SYSTEM_TIME_EXPIRED
    || item?.field_timed_out?.[field] === true
    || isSystemTimeExpired(item);
};

export const isPhase4DescriptorTimedOut = (item, index) => {
  const value = item?.words?.[index];
  if (isSystemTimeExpired({}, value)) return true;
  if (String(value ?? '').trim() && !isReservedClientTimeoutText(value)) return false;
  return item?.descriptor_system_states?.[index] === SYSTEM_TIME_EXPIRED
    || item?.descriptor_timed_out?.[index] === true
    || isSystemTimeExpired(item);
};

// Phase 4 keeps SYSTEM_TIME_EXPIRED as a detailed system state, but all
// reviewer-summary aggregation uses one public category label.
export const normalizePhase4SummaryCategory = (category, slotTimedOut = false) => {
  if (slotTimedOut || isSystemTimeExpired({}, category)) return 'Timeout';
  return category;
};

const isExplicitPhase4Category = (category, manuallySelected) =>
  manuallySelected === true && Boolean(String(category ?? '').trim());

// A coping category is stored once per favorite thing, so any genuinely
// expired required slot in that item makes its single aggregate contribution
// Timeout. Slot helpers retain the historical safeguard that populated client
// text wins over stale item-level timeout metadata.
export const isPhase4ItemTimedOut = (item) => {
  if (isPhase4FieldTimedOut(item, 'item') || isPhase4FieldTimedOut(item, 'what_is_it')) return true;
  const slotCount = Math.max(
    item?.words?.length || 0,
    item?.thematic_domains?.length || 0,
    item?.descriptor_system_states?.length || 0,
    item?.descriptor_timed_out?.length || 0,
  );
  for (let index = 0; index < slotCount; index += 1) {
    if (isPhase4DescriptorTimedOut(item, index)) return true;
  }
  return false;
};

export const getPhase4CopingAggregateCategory = (item) =>
  isExplicitPhase4Category(item?.coping_mechanism, item?.coping_mechanism_manual || item?.coping_mechanism_auto)
    ? normalizePhase4SummaryCategory(item.coping_mechanism, false)
    : normalizePhase4SummaryCategory(item?.coping_mechanism, isPhase4ItemTimedOut(item));

export const getPhase4ThematicAggregateCategory = (item, index) =>
  isExplicitPhase4Category(item?.thematic_domains?.[index], item?.thematic_domains_manual?.[index] || item?.thematic_domains_auto?.[index])
    ? normalizePhase4SummaryCategory(item.thematic_domains[index], false)
    : normalizePhase4SummaryCategory(item?.thematic_domains?.[index], isPhase4DescriptorTimedOut(item, index));

export const updatePhase4CopingCategory = (phase4Data, itemIndex, category, manual = true) => ({
  ...phase4Data,
  favorite_things: (phase4Data?.favorite_things || []).map((item, index) => index === itemIndex
    ? { ...item, coping_mechanism: category, coping_mechanism_manual: manual }
    : item),
});

export const updatePhase4ThematicCategory = (phase4Data, itemIndex, wordIndex, category, manual = true) => ({
  ...phase4Data,
  favorite_things: (phase4Data?.favorite_things || []).map((item, index) => {
    if (index !== itemIndex) return item;
    const domains = [...(item?.thematic_domains || [])];
    const manualSelections = [...(item?.thematic_domains_manual || [])];
    domains[wordIndex] = category;
    manualSelections[wordIndex] = manual;
    return { ...item, thematic_domains: domains, thematic_domains_manual: manualSelections };
  }),
});

export const getPhase4SummaryCounts = (favoriteThings = []) => {
  const mechanismCounts = {};
  const themeCounts = {};
  const increment = (counts, category) => {
    if (!category || category === 'null' || category === 'Pending') return;
    counts[category] = (counts[category] || 0) + 1;
  };

  (favoriteThings || []).filter(Boolean).forEach(item => {
    const mechanism = getPhase4CopingAggregateCategory(item);
    increment(mechanismCounts, mechanism);

    const domains = Array.isArray(item?.thematic_domains) ? item.thematic_domains : [];
    const slotCount = Math.max(
      domains.length,
      item?.words?.length || 0,
      item?.descriptor_system_states?.length || 0,
      item?.descriptor_timed_out?.length || 0,
    );
    for (let index = 0; index < slotCount; index += 1) {
      const domain = getPhase4ThematicAggregateCategory(item, index);
      increment(themeCounts, domain);
    }
  });

  return { mechanismCounts, themeCounts };
};
